'use client';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import useTranslation from '@components/Translation/useTranslation';
import { ConnectedFocusError } from 'focus-formik-error';

import { usePassword } from '@hooks/auth';
import { ErrorState, Input } from '@components/Form';
import useBasket from '@hooks/basket/useBasket';
import BaseButtonWithProgress from '@components/BaseButtonWithProgress';

import { initialValues, InputKeys, PASSWORD_REGEX } from '@repo/ui/src/modules/UI/constants';

interface Props {
  onSuccess?: () => void;
  email?: string;
}

const ResetPassword = ({ onSuccess, email }: Props) => {
  const { t } = useTranslation();
  const { trigger, isMutating } = usePassword('confirm');
  const { mutate: getBasket, isLoading } = useBasket();

  const onSubmit = async (
    { username, confirmation_code: confirmationCode, password }: typeof initialValues.resetPassword,
    { setStatus, resetForm }: FormikHelpers<typeof initialValues.resetPassword>,
  ) => {
    setStatus(null);

    try {
      await trigger({ username, confirmationCode: confirmationCode.toString(), password });
      resetForm();
      await getBasket();
      onSuccess?.();
    } catch (e: any) {
      setStatus(e?.response?.data?.message);
    }
  };

  const validationSchema = () =>
    Yup.object().shape({
      [InputKeys.EMAIL]: Yup.string()
        .email(
          t('auth.fields.emailInput.invalidText', undefined, {
            default: 'Invalid email.',
          }),
        )
        .required(
          t('auth.fields.emailInput.requiredText', undefined, {
            default: 'Email is required.',
          }),
        ),
      [InputKeys.CONFIRMATION_CODE]: Yup.string().required('Confirmation code is required'),
      [InputKeys.PASSWORD]: Yup.string()
        .required(
          t('auth.fields.passwordInput.requiredText', undefined, {
            default: 'Password is required.',
          }),
        )
        .matches(
          PASSWORD_REGEX,
          t('auth.fields.passwordInput.invalidText', undefined, {
            default: 'Password is Invalid.',
          }),
        ),
      [InputKeys.PASSWORD_CONFIRMATION]: Yup.string()
        .required(
          t('auth.fields.passwordConfirmationInput.requiredText', undefined, {
            default: 'Password confirmation is required.',
          }),
        )
        .oneOf(
          [Yup.ref(InputKeys.PASSWORD), null],
          t('auth.fields.passwordConfirmationInput.invalidText', undefined, {
            default: 'Passwords must match.',
          }),
        ),
    });

  const initVals = initialValues.resetPassword;
  if (initVals[InputKeys.EMAIL] && email) initVals[InputKeys.EMAIL] = email;

  return (
    <Formik validationSchema={validationSchema} onSubmit={onSubmit} initialValues={initVals}>
      {({ isSubmitting, status }) => (
        <Form className="tw-mb-7 tw-grid tw-gap-y-5 sm:tw-gap-y-7">
          <ConnectedFocusError />

          <Input
            required
            type="email"
            id={InputKeys.EMAIL}
            name={InputKeys.EMAIL}
            autoComplete="email"
            label={t('auth.fields.emailInput.label', undefined, {
              default: 'Email',
            })}
          />

          <Input
            required
            id="confirmation_code"
            name="confirmation_code"
            type="text"
            label={t('auth.fields.confirmationCodeInput.label', undefined, {
              default: 'Confirmation Code',
            })}
          />

          <Input
            id="password"
            required
            name="password"
            type="password"
            label={t('auth.fields.passwordInput.label', undefined, {
              default: 'Password',
            })}
          />

          <Input
            required
            id="passwordConfirmation"
            name="passwordConfirmation"
            type="password"
            label={t('auth.fields.passwordConfirmationInput.label', undefined, {
              default: 'Password Confirmation',
            })}
          />

          <ErrorState text={status} />

          <BaseButtonWithProgress
            size="large"
            type="submit"
            full
            processing={isSubmitting || isMutating || isLoading}
            processingText={''}
          >
            {t('auth.forgot.form.buttonText', undefined, { default: 'Confirm Password' })}
          </BaseButtonWithProgress>
        </Form>
      )}
    </Formik>
  );
};

export default ResetPassword;
